import Header from '@/components/elements/Header/Header';
import { Button } from '@/components/elements/forms/buttons';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Icon from '@/components/icons/Icon';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import useCheckoutExperiment from '@/hooks/experiment/useCheckoutExperiment';
import { LoginContextType } from '@/hooks/useLogin';
import { _s } from '@/locale';
import { _t } from '@/locale/i18n';
import { LoginSignupFlowInitiatedFrom } from '@/types/analytics';
import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

const baseTranslationKey = `components.templates.checkout.UserLogin`;

type UserLoginProps = Omit<
  LoginContextType,
  'handleLogin' | 'updateView' | 'view' | 'user' | 'handleEmailResendClicked' | 'show' | 'onOpen' | 'onClose' | 'flow'
> & {
  initiatedFrom: LoginSignupFlowInitiatedFrom;
  continueAsGuest?: () => void;
};

const UserLogin = ({
  email,
  updateEmail,
  handleEmailSubmit,
  continueAsGuest,
  initiatedFrom,
  clearLoginState,
  onAbortServerRequest,
}: UserLoginProps) => {
  const { pathname } = useLocation();
  const allowsContinueAsGuest = continueAsGuest !== undefined;
  const titleTranslation = _t(`${baseTranslationKey}.title${allowsContinueAsGuest ? 'AsGuest' : ''}`);

  const { variant: checkoutVariant } = useCheckoutExperiment();
  const isCheckoutRedesign = checkoutVariant !== 'control';

  const getCtaLabel = () => {
    if (email.isChecked && !email.isRegistered) {
      return _t(`register`);
    }
    return _t(`${baseTranslationKey}.loginCta${allowsContinueAsGuest ? 'AsGuest' : ''}`);
  };

  const handleLoginClick = () => {
    handleEmailSubmit({ redirectUrl: pathname });
  };

  const handleContinueAsGuestClick = () => {
    loginTrackingSession.track({ step: 'continue_as_guest', flow: 'login' });
    loginTrackingSession.end();
    onAbortServerRequest();
    clearLoginState();
    continueAsGuest();
  };

  const handleTermsClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    loginTrackingSession.track({ step: 'terms_and_conditions', flow: 'login' });
  };

  useEffect(() => {
    if (loginTrackingSession.getInitiatedFrom() !== initiatedFrom) {
      loginTrackingSession.start(initiatedFrom, { force: true });
      loginTrackingSession.track({ step: 'initiated', flow: 'login' });
    }
  }, []);

  return (
    <div className="gap-md px-lg flex flex-col">
      <div className="py-md">
        <Header size="md" label={titleTranslation} />
      </div>
      <div className="gap-md flex flex-col">
        <ListInput
          id="email"
          type="email"
          value={email.value}
          label={_s(`${baseTranslationKey}.input.label`)}
          placeholder="namn@exempel.com"
          leftIcon={<Icon variant="email" />}
          onChange={(e) => updateEmail(e.target.value)}
          error={email.error && email.submitting ? _s(`RequestToken.input.error`) : undefined}
          floatStyle={isCheckoutRedesign}
          leftPadding={false}
          rightPadding={false}
        />
        {email.isChecked && !email.isRegistered && (
          <Alert
            leftSlot={<Icon size="sm" variant="info-circle" />}
            variant="information"
            verticalAlign="top"
            middleSlot={
              <p>
                {_s(`RequestToken.newEmailAccount.notFound`)} <strong>{email.value}</strong>
              </p>
            }
          />
        )}
        <Button
          disabled={email.error || email.submitting}
          label={getCtaLabel()}
          onClick={handleLoginClick}
          size="md"
          variant="primary"
        />
        {allowsContinueAsGuest && (
          <Button
            label={_s(`${baseTranslationKey}.continueAsGuest`)}
            onClick={handleContinueAsGuestClick}
            size="sm"
            variant="link"
          />
        )}
        <p className="text-black-600 text-m">
          {_s(`RequestToken.terms.text`)}
          <br />
          <a onClick={handleTermsClick} href="/articles/terms-booking-consumer" target="_blank" className="underline">
            {_s(`RequestToken.terms.link`)}
          </a>
        </p>
      </div>
    </div>
  );
};

export default UserLogin;
